import { Component, DestroyRef, HostListener, OnInit, PLATFORM_ID, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterEvent, RouterModule, RouterOutlet } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { AnalyticsService, AuthService, User, UserService, UserStateService } from 'riterz-core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AppService } from './services/app.service';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { isPlatformBrowser } from '@angular/common';
import { AvatarModule } from 'primeng/avatar';
import { Popover } from 'primeng/popover';
import { VerifiedEmailComponent } from './modals/verified-email/verified-email.component';
import { WelcomeComponent } from './modals/welcome/welcome.component';
import { OrganizationSubmitComponent } from './modals/organization-submit/organization-submit.component';
import { VerifyEmailComponent } from './modals/verify-email/verify-email.component';
import { PasswordChangeComponent } from './modals/password-change/password-change.component';
import { SearchComponent } from './components/search/search.component';
import { OrganizationSwitcherComponent } from './components/organization-switcher/organization-switcher.component';
import { DividerModule } from "primeng/divider";
import { TermsOfServiceComponent } from './modals/terms-of-service/terms-of-service.component';

@Component({
  selector: 'rt-root',
  imports: [
    RouterModule,
    RouterOutlet,
    ButtonModule,
    AvatarModule,
    Popover,
    ToastModule,
    ConfirmDialogModule,
    SearchComponent,
    DividerModule,
    OrganizationSwitcherComponent
],
  standalone: true,
  templateUrl: './app.component.html',
  providers: [
    DialogService,
    AnalyticsService,
    MessageService,
    ConfirmationService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class AppComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  private appService = inject(AppService);
  private dialogService = inject(DialogService);
  private authService = inject(AuthService);
  private userStateService = inject(UserStateService);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private analyticsService = inject(AnalyticsService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private destroyRef = inject(DestroyRef);

  protected title = 'Riterz';

  readonly currentUser = signal<User|null>(null);

  readonly loading = signal<boolean>(false);

  readonly isAuthRoute = signal<boolean>(false);

  readonly isSuperAdmin = signal<boolean>(false);
  readonly loadingUser = signal<boolean>(true);

  ngOnInit(): void {
    // Track the shared subject so the header reflects every writer (modals, account
    // page, invitation acceptance), not just this component's own fetch.
    this.userStateService
      .currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.currentUser.set(user);
        this.isSuperAdmin.set(!!user?.roles?.some((role) => role.name === 'super-admin'));
      });

    this.getCurrentUser();

    this.analyticsService.init();

    this.appService
      .toast
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((message: any) => {
        if (message) {
          this.messageService.add(message);
        }
      });

    this.appService
      .dialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dialog: any) => {
        if (dialog) {
          this.confirmationService.confirm(dialog);
        }
      });

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (params['refresh_token'] && params['access_token']) {
          const redirectUrl = params['redirect_url'];
          if (redirectUrl && this.authService.isTrustedOauthRedirect(redirectUrl)) {
            // Set cookies via authService directly — skip the component's getCurrentUser(); the full-page redirect would abort it.
            this.authService.setRiterzTokens(params['refresh_token'], params['access_token']);
            this.authService.redirectToOauthSession(redirectUrl);
            return;
          }
          this.setRiterzTokens(params['refresh_token'], params['access_token']);
          this.router.navigate(['/'], {
            replaceUrl: true,
          })
        }
        if (params['verify_email']) {
          this.verifyEmail(params['verify_email']);
          this.router.navigate([], {
            queryParams: {
              'verify_email': null,
            },
            queryParamsHandling: 'merge'
          })
        }
        if (params['invitation_token']) {
          this.handleInvitation(params['invitation_token']);
          this.router.navigate([], {
            queryParams: {
              'invitation_token': null,
            },
            queryParamsHandling: 'merge'
          })
        }
      });

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((routerEvent) => {
        this.checkRouterEvent(routerEvent as RouterEvent);
      });
  }

  checkRouterEvent(routerEvent: RouterEvent): void {
    if (routerEvent instanceof NavigationStart) {
      this.loading.set(true);
    }

    if (routerEvent instanceof NavigationEnd ||
        routerEvent instanceof NavigationCancel ||
        routerEvent instanceof NavigationError
    ) {
      this.loading.set(false);
    }

    if (routerEvent instanceof NavigationEnd) {
      this.isAuthRoute.set(routerEvent.urlAfterRedirects?.startsWith('/auth') ?? false);
    }
  }

  getCurrentUser(): void {
    const params = {
      include: 'organizations,roles'
    }

    this.loadingUser.set(true);
    this.userStateService
      .getCurrentUser(params)
      .subscribe({
        next: (response) => {
          // No access token — nothing to load. Must still clear the flag, otherwise the
          // whole header user block stays hidden until a full page reload.
          if (!response) {
            this.loadingUser.set(false);
            return;
          }

          // `currentUser` / `isSuperAdmin` are set by the currentUser subscription in ngOnInit.
          if (this.currentUser() && !this.currentUser()?.accepted_terms_of_service) {
            this.termsAndConditions();
          }

          this.loadingUser.set(false);

          // Redirect non-approved users
          const status = this.currentUser()?.status;
          if (this.currentUser() && (status === 'pending' || status === 'rejected')) {
            const url = this.router.url;
            const allowedPrefixes = ['/pending-approval', '/registration-rejected', '/account', '/auth'];
            const isAllowed = allowedPrefixes.some(prefix => url.startsWith(prefix));

            if (!isAllowed) {
              if (status === 'rejected') {
                this.router.navigate(['/registration-rejected']);
              } else {
                this.router.navigate(['/pending-approval']);
              }
            }
          }
        },
        error: (error) => {
          this.loadingUser.set(false);

          // Guards wait on `currentUser` with `filter(u => u !== null), take(1)`, so a
          // dead session would hang every navigation forever. Drop it and bounce to login.
          if (error?.status === 401) {
            this.authService.clearSession();
            this.router.navigate(['/auth/login']);
          }
        }
      });
  }

  handleInvitation(invitationToken: string): void {
    if (!isPlatformBrowser(this.platformId)) { return; }
    if (!invitationToken) { return; }

    const isLoggedIn = !!this.authService.getToken();

    if (!isLoggedIn) {
      // Redirect to register page with invitation token
      this.router.navigate(['/auth/register'], {
        queryParams: { invitation_token: invitationToken }
      });
      return;
    }

    this.userService
      .getInvitation(invitationToken, { include: 'organization' })
      .subscribe({
        next: (response) => {
          const invitation = (response as { data: { organization: { name: string } } | null })?.data;

          if (!invitation) { return; }

          this.confirmationService.confirm({
            message: `Do you want to accept this invitation to join ${invitation.organization.name}?`,
            header: 'Accept Invitation',
            acceptLabel: 'Accept',
            rejectLabel: 'Close',
            accept: () => {
              this.userService
                .connectInvitation(invitationToken)
                .subscribe({
                  next: () => {
                    this.appService.showToast({
                      severity: 'success',
                      summary: `Success`,
                      detail: 'Invitation successfully accepted.'
                    });
                    this.getCurrentUser();
                  },
                  error: (error: any) => {
                    this.appService.showToast({
                      severity: 'error',
                      summary: `Error`,
                      detail: error.message
                    });
                  }
                });
            }
          });
        },
        error: (error) => {
          this.appService.showToast({
            severity: 'error',
            summary: `Error`,
            detail: error.message
          });
        }
      });
  }

  welcome(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.dialogService.open(WelcomeComponent, {
        styleClass: 'rt-welcome',
        dismissableMask: true,
        width: '36rem',
        data: {
          verified: true,
          email: this.currentUser()?.email,
          name: this.currentUser()?.first_name
        }
      });
    }
  }

  termsAndConditions(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.dialogService.open(TermsOfServiceComponent, {
        header: 'Terms & Conditions update',
        modal: true,
        closable: false,
        dismissableMask: false,
        closeOnEscape: false,
        width: '50rem'
      });
    }
  }

  logout(): void {
    this.authService
      .logout()
      .subscribe(() => {
        this.currentUser.set(null);
        this.router.navigate(['/auth/login']);
      });
  }

  setRiterzTokens(refresh_token: string, access_token: string): void {
    this.authService.setRiterzTokens(refresh_token, access_token);
    this.getCurrentUser();
  }

  verifyEmail(data: string): void {
    if (isPlatformBrowser(this.platformId)) {
      const params = decodeURIComponent(data).split('|');
      const id = params[0];
      const token = params[1];
      this.authService
        .verifyEmail(id, token)
        .subscribe({
          next: () => {
            this.dialogService.open(VerifiedEmailComponent, {
              header: 'Verify Organisation',
              width: '40rem'
            });
            this.getCurrentUser();
          },
          error: (error) => {
            this.appService.showToast({
              severity: 'error',
              summary: `Error`,
              detail: error.message
            });
          }
        });
    }
  }

  openProfileForm(): void {
    if (!this.currentUser()) {
      return;
    }

    if (!this.currentUser()?.email_verified_at) {
      this.dialogService.open(VerifyEmailComponent, {
        header: 'Verify Email',
        width: '40rem',
      });
      return;
    }

    this.dialogService.open(OrganizationSubmitComponent, {
      header: 'Profile Submission',
      width: '40rem',
      closable: true,
      modal: true
    });
  }

  passwordChange(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.dialogService.open(PasswordChangeComponent, {
        header: 'Update Your Password',
        width: '40rem',
        closable: true,
        modal: true,
        dismissableMask: true,
      });
    }
  }


  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'k' && (event.ctrlKey || event.metaKey)) {
      this.appService.showSearch();
    }
  }

  showSearch(): void {
    this.appService.showSearch();
  }

}
